import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  PhotoIcon, 
  VideoCameraIcon, 
  SpeakerWaveIcon,
  DocumentIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.mythicalarc.com/api'
  : 'http://localhost:8000/api';

const MediaGallery = () => {
  const [mediaItems, setMediaItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchMediaLibrary();
    fetchMediaStats();
  }, []);

  useEffect(() => {
    filterItems();
  }, [mediaItems, searchQuery, selectedType]);

  const fetchMediaLibrary = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/media/library?limit=100`);
      setMediaItems(response.data.media_items);
    } catch (error) {
      console.error('Failed to fetch media library:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMediaStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/media/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch media stats:', error);
    }
  };

  const filterItems = () => {
    let filtered = mediaItems;

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(item => item.media_type === selectedType);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.content.toLowerCase().includes(query) ||
        (item.caption && item.caption.toLowerCase().includes(query)) ||
        (item.extracted_text && item.extracted_text.toLowerCase().includes(query)) ||
        (item.metadata.original_filename && 
         item.metadata.original_filename.toLowerCase().includes(query))
      );
    }

    setFilteredItems(filtered);
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case 'image': return <PhotoIcon className="w-6 h-6 text-blue-500" />;
      case 'video': return <VideoCameraIcon className="w-6 h-6 text-purple-500" />;
      case 'audio': return <SpeakerWaveIcon className="w-6 h-6 text-green-500" />;
      default: return <DocumentIcon className="w-6 h-6 text-gray-500" />;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats.by_type && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Media Library Stats</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.total_items}</div>
              <div className="text-sm text-gray-600">Total Items</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.by_type.image || 0}</div>
              <div className="text-sm text-gray-600">Images</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.by_type.video || 0}</div>
              <div className="text-sm text-gray-600">Videos</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{stats.by_type.audio || 0}</div>
              <div className="text-sm text-gray-600">Audio</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">{stats.by_type.document || 0}</div>
              <div className="text-sm text-gray-600">Documents</div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search your media..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            />
          </div>

          {/* Filter */}
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="audio">Audio</option>
              <option value="document">Documents</option>
            </select>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredItems.length} of {mediaItems.length} items
        </div>
      </div>

      {/* Media Grid */}
      {filteredItems.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
              {/* Thumbnail */}
              <div className="aspect-video bg-gray-100 relative">
                {item.thumbnail ? (
                  <img
                    src={item.thumbnail}
                    alt={item.caption || 'Thumbnail'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {getMediaIcon(item.media_type)}
                  </div>
                )}
                
                {/* Media Type Badge */}
                <div className="absolute top-2 left-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    item.media_type === 'image' ? 'bg-blue-100 text-blue-800' :
                    item.media_type === 'video' ? 'bg-purple-100 text-purple-800' :
                    item.media_type === 'audio' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {item.media_type}
                  </span>
                </div>

                {/* Duration for video/audio */}
                {item.duration && (
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                    {formatDuration(item.duration)}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                {/* Filename */}
                <h3 className="font-medium text-gray-900 truncate mb-2">
                  {item.metadata.original_filename || 'Untitled'}
                </h3>

                {/* Caption */}
                {item.caption && (
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {item.caption}
                  </p>
                )}

                {/* Extracted Text Preview */}
                {item.extracted_text && item.extracted_text.length > 0 && (
                  <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                    📝 {item.extracted_text.substring(0, 100)}...
                  </p>
                )}

                {/* Timestamp */}
                <div className="flex items-center text-xs text-gray-500">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  {formatDate(item.timestamp)}
                </div>

                {/* Video Frame Count */}
                {item.frames_count > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    🎬 {item.frames_count} frames analyzed
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <PhotoIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No media found</h3>
          <p className="text-gray-500">
            {searchQuery || selectedType !== 'all' 
              ? 'Try adjusting your search or filter criteria'
              : 'Upload some files to get started'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default MediaGallery;
